import { useState, useEffect, useRef, useCallback } from "react";
import {
  LIVEBRIDGE_ROOM_ID,
  sendLiveBridgeMessage,
  resetLiveBridgeSession,
  translateLiveBridgeText
} from "./liveBridgeApi";
import { mergeLiveBridgeTranscript, shouldTranslateLiveBridgeDraft } from "./liveBridgeTranscript";

const LIVEBRIDGE_PREVIEW_DEBOUNCE_MS = 420;

const LIVEBRIDGE_DEMO_LINES = [
  { englishText: "Can you see me clearly on WeChat now?", chineseText: "你现在在微信视频里能清楚看到我吗？" },
  { englishText: "Grandma, I finished my school project today.", chineseText: "奶奶，我今天完成了学校项目。" },
  { englishText: "We are going to visit you this weekend if the weather is good.", chineseText: "如果天气好，这个周末我们会去看你。" },
  { englishText: "I miss you and I want to show you my drawing.", chineseText: "我很想你，我想给你看看我的画。" }
];

/**
 * useNetworkSync
 *
 * Owns: message push, session reset, preview debounce, translation fill.
 * Coordinates with useDraftState and useViewerSession via stable props.
 *
 * Props from useDraftState:
 *   draft, chineseDraft, latestEnglish, latestChinese, sender
 *   draftRef, chineseDraftRef, latestEnglishRef, latestChineseRef
 *   setDraft, setChineseDraft, setLatestEnglish, setLatestChinese, clearDrafts
 *
 * Props from useViewerSession:
 *   viewerPassword, setConnectionStatus, setPreview
 *   appendLocalMessage, applyRoomPayload, loadMessages, pushPreview
 *
 * Other:
 *   inputStatus — from useRecognition
 *   t           — i18n function
 *   page        — current page string
 */
export function useNetworkSync({ page, t, draft, viewer, inputStatus }) {
  const {
    liveBridgeDraft,
    liveBridgeChineseDraft,
    liveBridgeLatestEnglish,
    liveBridgeLatestChinese,
    liveBridgeSender,
    liveBridgeDraftRef,
    liveBridgeChineseDraftRef,
    liveBridgeLatestEnglishRef,
    liveBridgeLatestChineseRef,
    setLiveBridgeDraft,
    setLiveBridgeChineseDraft,
    setLiveBridgeLatestEnglish,
    setLiveBridgeLatestChinese,
    clearDrafts
  } = draft;

  const {
    liveBridgeViewerPassword,
    setLiveBridgeConnectionStatus,
    setLiveBridgePreview,
    appendLiveBridgeLocalMessage,
    applyLiveBridgeRoomPayload,
    loadLiveBridgeMessages,
    pushLiveBridgePreview
  } = viewer;

  const [liveBridgeSending, setLiveBridgeSending] = useState(false);
  const [liveBridgeTranslating, setLiveBridgeTranslating] = useState(false);
  const [liveBridgeSendError, setLiveBridgeSendError] = useState("");
  const [liveBridgeSendSuccess, setLiveBridgeSendSuccess] = useState("");

  const liveBridgePreviewTimerRef = useRef(null);
  const liveBridgePreviewRequestIdRef = useRef(0);
  const liveBridgeTestMessageIndexRef = useRef(0);
  const liveBridgeAutoEnglishSourceRef = useRef("");
  const liveBridgeAutoChineseSourceRef = useRef("");

  // Preview debounce — fires on every draft/latest change while on the input page.
  useEffect(() => {
    if (page !== "liveBridgeInput") {
      return undefined;
    }

    const clearPreviewTimer = () => {
      if (liveBridgePreviewTimerRef.current) {
        window.clearTimeout(liveBridgePreviewTimerRef.current);
        liveBridgePreviewTimerRef.current = null;
      }
    };

    clearPreviewTimer();

    const englishText = mergeLiveBridgeTranscript(liveBridgeDraft, liveBridgeLatestEnglish, "en-US");
    const chineseText = mergeLiveBridgeTranscript(liveBridgeChineseDraft, liveBridgeLatestChinese, "zh-CN");
    const sender = String(liveBridgeSender || "").trim() || "Guest";

    if (!englishText && !chineseText) {
      setLiveBridgePreview(null);
      setLiveBridgeLatestEnglish("");
      setLiveBridgeLatestChinese("");
      liveBridgeAutoEnglishSourceRef.current = "";
      liveBridgeAutoChineseSourceRef.current = "";
      liveBridgePreviewTimerRef.current = window.setTimeout(() => {
        pushLiveBridgePreview({ sender, englishText: "", chineseText: "" }).catch(() => {});
      }, 80);
      return clearPreviewTimer;
    }

    const delay = inputStatus === "listening" ? LIVEBRIDGE_PREVIEW_DEBOUNCE_MS : 180;

    liveBridgePreviewTimerRef.current = window.setTimeout(async () => {
      const requestId = liveBridgePreviewRequestIdRef.current + 1;
      liveBridgePreviewRequestIdRef.current = requestId;

      let nextEnglish = mergeLiveBridgeTranscript(liveBridgeDraftRef.current, liveBridgeLatestEnglish, "en-US");
      let nextChinese = mergeLiveBridgeTranscript(liveBridgeChineseDraftRef.current, liveBridgeLatestChinese, "zh-CN");
      const hasManualEnglish = Boolean(String(liveBridgeDraftRef.current || "").trim());
      const hasManualChinese = Boolean(String(liveBridgeChineseDraftRef.current || "").trim());

      try {
        if (
          nextEnglish &&
          shouldTranslateLiveBridgeDraft(nextEnglish, "en-US", inputStatus === "listening") &&
          !hasManualChinese &&
          (liveBridgeAutoChineseSourceRef.current !== nextEnglish || !nextChinese)
        ) {
          const payload = await translateLiveBridgeText({ text: nextEnglish, sourceLanguage: "en", targetLanguage: "zh" });
          const translatedText = String(payload?.translatedText || "").trim();

          if (requestId !== liveBridgePreviewRequestIdRef.current) return;
          if (translatedText) {
            nextChinese = translatedText;
            liveBridgeAutoChineseSourceRef.current = nextEnglish;
          }
        } else if (
          nextChinese &&
          shouldTranslateLiveBridgeDraft(nextChinese, "zh-CN", inputStatus === "listening") &&
          !hasManualEnglish &&
          (liveBridgeAutoEnglishSourceRef.current !== nextChinese || !nextEnglish)
        ) {
          const payload = await translateLiveBridgeText({ text: nextChinese, sourceLanguage: "zh", targetLanguage: "en" });
          const translatedText = String(payload?.translatedText || "").trim();

          if (requestId !== liveBridgePreviewRequestIdRef.current) return;
          if (translatedText) {
            nextEnglish = translatedText;
            liveBridgeAutoEnglishSourceRef.current = nextChinese;
          }
        }

        setLiveBridgeLatestEnglish(nextEnglish);
        setLiveBridgeLatestChinese(nextChinese);
        setLiveBridgePreview({ sender, englishText: nextEnglish, chineseText: nextChinese, updatedAt: new Date().toISOString() });

        await pushLiveBridgePreview({ sender, englishText: nextEnglish, chineseText: nextChinese });
      } catch (_error) {
        if (requestId === liveBridgePreviewRequestIdRef.current) {
          setLiveBridgeLatestEnglish(nextEnglish);
          setLiveBridgeLatestChinese(nextChinese);
          setLiveBridgePreview({ sender, englishText: nextEnglish, chineseText: nextChinese, updatedAt: new Date().toISOString() });
        }
      }
    }, delay);

    return clearPreviewTimer;
  }, [
    liveBridgeChineseDraft,
    liveBridgeChineseDraftRef,
    liveBridgeDraft,
    liveBridgeDraftRef,
    liveBridgeLatestChinese,
    liveBridgeLatestChineseRef,
    liveBridgeLatestEnglish,
    liveBridgeLatestEnglishRef,
    liveBridgeSender,
    inputStatus,
    page,
    pushLiveBridgePreview,
    setLiveBridgeLatestChinese,
    setLiveBridgeLatestEnglish,
    setLiveBridgePreview
  ]);

  const pushLiveBridgeMessage = useCallback(
    async ({ englishText, chineseText, sender }) => {
      if ((!englishText && !chineseText) || liveBridgeSending) {
        return;
      }

      setLiveBridgeSending(true);
      setLiveBridgeSendError("");
      setLiveBridgeSendSuccess("");
      setLiveBridgeConnectionStatus("syncing");
      setLiveBridgeLatestEnglish(englishText || "");
      setLiveBridgeLatestChinese(chineseText || "");

      try {
        const payload = await sendLiveBridgeMessage({
          sender: sender || liveBridgeSender,
          englishText,
          chineseText,
          roomId: LIVEBRIDGE_ROOM_ID
        });

        appendLiveBridgeLocalMessage(payload.message, payload.session);
        setLiveBridgePreview(null);
        setLiveBridgeLatestEnglish(payload?.message?.englishText || englishText || "");
        setLiveBridgeLatestChinese(payload?.message?.chineseText || chineseText || "");
        setLiveBridgeDraft("");
        setLiveBridgeChineseDraft("");
        setLiveBridgeSendSuccess(t("livebridge_sent"));
        setLiveBridgeConnectionStatus("connected");

        if (liveBridgeViewerPassword) {
          await loadLiveBridgeMessages(liveBridgeViewerPassword);
        }

        return payload;
      } catch (error) {
        setLiveBridgeConnectionStatus("disconnected");
        setLiveBridgeSendError(
          error instanceof Error && error.message ? error.message : "LiveBridge send failed."
        );
      } finally {
        setLiveBridgeSending(false);
      }
    },
    [
      appendLiveBridgeLocalMessage,
      liveBridgeSender,
      liveBridgeSending,
      liveBridgeViewerPassword,
      loadLiveBridgeMessages,
      setLiveBridgeChineseDraft,
      setLiveBridgeConnectionStatus,
      setLiveBridgeDraft,
      setLiveBridgeLatestChinese,
      setLiveBridgeLatestEnglish,
      setLiveBridgePreview,
      t
    ]
  );

  const handleLiveBridgeSend = useCallback(async () => {
    const englishText = String(liveBridgeDraft || "").trim();
    const chineseText = String(liveBridgeChineseDraft || "").trim();

    if (!englishText && !chineseText) return;

    await pushLiveBridgeMessage({ sender: liveBridgeSender, englishText, chineseText });
  }, [liveBridgeDraft, liveBridgeChineseDraft, liveBridgeSender, pushLiveBridgeMessage]);

  const handleLiveBridgeFillMissingSide = useCallback(async () => {
    const englishText = String(liveBridgeDraft || "").trim();
    const chineseText = String(liveBridgeChineseDraft || "").trim();

    if (liveBridgeSending || liveBridgeTranslating) return;

    if (!englishText && !chineseText) {
      setLiveBridgeSendError(t("livebridge_fill_missing_empty"));
      setLiveBridgeSendSuccess("");
      return;
    }

    if (englishText && chineseText) {
      setLiveBridgeSendError(t("livebridge_fill_missing_full"));
      setLiveBridgeSendSuccess("");
      return;
    }

    setLiveBridgeTranslating(true);
    setLiveBridgeSendError("");
    setLiveBridgeSendSuccess("");
    setLiveBridgeConnectionStatus("syncing");

    try {
      if (englishText) {
        const payload = await translateLiveBridgeText({ text: englishText, sourceLanguage: "en", targetLanguage: "zh" });
        const translatedText = String(payload?.translatedText || "").trim();

        if (!translatedText) throw new Error(t("livebridge_fill_missing_failed"));

        setLiveBridgeChineseDraft(translatedText);
        setLiveBridgeLatestEnglish(englishText);
        setLiveBridgeLatestChinese(translatedText);
      } else {
        const payload = await translateLiveBridgeText({ text: chineseText, sourceLanguage: "zh", targetLanguage: "en" });
        const translatedText = String(payload?.translatedText || "").trim();

        if (!translatedText) throw new Error(t("livebridge_fill_missing_failed"));

        setLiveBridgeDraft(translatedText);
        setLiveBridgeLatestChinese(chineseText);
        setLiveBridgeLatestEnglish(translatedText);
      }

      setLiveBridgeConnectionStatus("connected");
      setLiveBridgeSendSuccess(t("livebridge_fill_missing_ready"));
    } catch (error) {
      setLiveBridgeConnectionStatus("disconnected");
      setLiveBridgeSendError(
        error instanceof Error && error.message ? error.message : t("livebridge_fill_missing_failed")
      );
    } finally {
      setLiveBridgeTranslating(false);
    }
  }, [
    liveBridgeDraft,
    liveBridgeChineseDraft,
    liveBridgeSending,
    liveBridgeTranslating,
    setLiveBridgeChineseDraft,
    setLiveBridgeConnectionStatus,
    setLiveBridgeDraft,
    setLiveBridgeLatestChinese,
    setLiveBridgeLatestEnglish,
    t
  ]);

  const handleLiveBridgeSendTestMessage = useCallback(async () => {
    const sample = LIVEBRIDGE_DEMO_LINES[liveBridgeTestMessageIndexRef.current % LIVEBRIDGE_DEMO_LINES.length];
    liveBridgeTestMessageIndexRef.current += 1;

    await pushLiveBridgeMessage({
      sender: liveBridgeSender || "Parent",
      englishText: sample.englishText,
      chineseText: sample.chineseText
    });
  }, [liveBridgeSender, pushLiveBridgeMessage]);

  const handleLiveBridgeStartNewSession = useCallback(async () => {
    if (liveBridgeSending) return;

    setLiveBridgeSending(true);
    setLiveBridgeSendError("");
    setLiveBridgeSendSuccess("");
    setLiveBridgeConnectionStatus("syncing");

    try {
      const payload = await resetLiveBridgeSession({ roomId: LIVEBRIDGE_ROOM_ID });
      applyLiveBridgeRoomPayload(payload);
      setLiveBridgePreview(null);
      setLiveBridgeLatestEnglish("");
      setLiveBridgeLatestChinese("");
      setLiveBridgeDraft("");
      setLiveBridgeChineseDraft("");
      setLiveBridgeSendSuccess(t("livebridge_session_reset"));
      setLiveBridgeConnectionStatus("connected");
    } catch (error) {
      setLiveBridgeConnectionStatus("disconnected");
      setLiveBridgeSendError(
        error instanceof Error && error.message ? error.message : "LiveBridge session reset failed."
      );
    } finally {
      setLiveBridgeSending(false);
    }
  }, [
    applyLiveBridgeRoomPayload,
    liveBridgeSending,
    setLiveBridgeChineseDraft,
    setLiveBridgeConnectionStatus,
    setLiveBridgeDraft,
    setLiveBridgeLatestChinese,
    setLiveBridgeLatestEnglish,
    setLiveBridgePreview,
    t
  ]);

  const handleLiveBridgeClearDrafts = useCallback(() => {
    clearDrafts();
    setLiveBridgePreview(null);
    setLiveBridgeSendError("");
    setLiveBridgeSendSuccess("");
    pushLiveBridgePreview({ sender: liveBridgeSender, englishText: "", chineseText: "" }).catch(() => {});
  }, [clearDrafts, liveBridgeSender, pushLiveBridgePreview, setLiveBridgePreview]);

  return {
    liveBridgeSending,
    liveBridgeTranslating,
    liveBridgeSendError,
    liveBridgeSendSuccess,
    handleLiveBridgeSend,
    handleLiveBridgeFillMissingSide,
    handleLiveBridgeSendTestMessage,
    handleLiveBridgeStartNewSession,
    handleLiveBridgeClearDrafts
  };
}
